import DatePicker from 'antd/lib/date-picker';
import Icon from 'antd/lib/icon';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Tag from 'antd/lib/tag';
import { ColumnProps } from 'antd/lib/table';
import Tooltip from 'antd/lib/tooltip';
import Upload from 'antd/lib/upload';
import moment from 'moment';
import * as React from 'react';
import { tokens } from 'src/design-system';
import Asterisk from 'src/components/common/Asterisk';
import {
  ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT,
  ADDITIONAL_CHARGE_DATE_WIRE_FORMAT,
  ADDITIONAL_CHARGE_GRID_LABELS,
  LINE_STATUS_COLORS,
  LINE_STATUS_LABELS,
} from './AdditionalChargeGrids.constants';
import {
  buildExclusiveGroupMembers,
  buildMirrorGroupMembers,
  computeThirdPartyInvoiceTotal,
  isColumnEditableForLine,
  isExclusiveMemberLocked,
  isQuantityDimEditable,
  resolveLineFieldValue,
} from './AdditionalChargeColumnRegistry';
import {
  lineErrors,
  resolveDisplayCurrency,
  resolveDisplayPcd,
  resolveDisplayQuantity,
  resolveDisplayRate,
  resolveInrCell,
} from './AdditionalChargeResolution';
import {
  AcLineError,
  AcQuantityDimResult,
  AdditionalChargeColumn,
  AdditionalChargeEditableFieldKey,
  AdditionalChargeLineDraftEdits,
  AdditionalChargeLineDto,
  AdditionalChargePendingAttachment,
  AdditionalChargeQuantityDraftInputs,
} from './AdditionalChargeGrids.types';

/**
 * Additional Vendor Charges — Part 2 registry-to-renderer resolution (ISS-291749 / C2).
 *
 * `CELL_RENDERERS` is keyed by the registry's column `key` — the same key `GET
 * /additional-charges/column-config` uses. A seventh `(chargeType, applicabilityLevel)` shape composed
 * from these same column keys renders correctly with zero changes here; only a genuinely new column key
 * would need a new renderer entry.
 *
 * This file is deliberately not the `.html.tsx` for this feature — it holds resolution logic (which
 * renderer a column key maps to, whether a cell is editable) alongside the JSX each renderer produces,
 * per CLAUDE.md's split ("registry-to-renderer logic belongs in a .tsx/utils file, not the .html.tsx").
 * `AdditionalChargeGrids.html.tsx` only ever receives the already-built `ColumnProps[]` from here.
 */

export interface AdditionalChargeCellFunctions {
  onFieldChange: (lineId: string, field: AdditionalChargeEditableFieldKey, value: string | null) => void;
  onUploadAttachment: (line: AdditionalChargeLineDto, file: File) => void;
  onOpenAttachment: (attachment: AdditionalChargePendingAttachment) => void;
  /** A not-yet-saved upload staged locally for this line — shown until the next Save persists it. */
  getPendingAttachment: (lineId: string) => AdditionalChargePendingAttachment | undefined;
  isUploadingLine: (lineId: string) => boolean;
  /** ISS-292248 Task 4: stages a vendor-typed value for one quantity dim (`resolution.quantity.dims[]`
   * `code`) on one line. Optional so a caller/test that never exercises the quantity cell (every fixture
   * before this task had no live `resolution.quantity`) doesn't have to supply it. */
  onQuantityInputChange?: (lineId: string, dimCode: string, value: string) => void;
}

interface CellRenderContext {
  draftEdits: AdditionalChargeLineDraftEdits;
  functions: AdditionalChargeCellFunctions;
  isAttachmentMandatory: boolean;
  isReadOnly: boolean;
  exclusiveGroupMembers: Record<string, string[]>;
  mirrorGroupMembers: Record<string, string[]>;
  pcd: string | null;
  /** ISS-292248: per-container PCD for CONTAINER-level vendor types (mirrors `pcd`), consumed by
   * `resolveDisplayPcd` for the PCD cell's fallback chain. */
  pcdByContainer?: Record<string, string | null>;
  exchangeRates: Record<string, number>;
  thirdPartyVendors: Array<{ code: string; name: string }>;
  currencyOptions: string[];
  /** Token-governed width classes for the two Selects below (ISS-292248 review fix, MINOR 8), sourced
   * from `AdditionalChargeGrids.styles.ts`. Optional so a caller (or a test) that doesn't care about
   * width can omit them without the Select crashing. */
  currencySelectClassName?: string;
  thirdPartyVendorSelectClassName?: string;
  /** ISS-292248 Task 4: staged (not-yet-saved) quantity-dim edits, lineId → dim `code` → typed value —
   * parallel to `draftEdits`, consulted by `renderQuantityCell` so a dim input shows the vendor's
   * in-progress typing rather than resetting to `dim.final` on every keystroke. Defaulted to `{}` by
   * `buildAntColumns` so a caller that never stages a quantity edit doesn't have to pass it. */
  draftQuantityInputs: AdditionalChargeQuantityDraftInputs;
}

/**
 * The payload/master-data values `buildAntColumns` cannot derive from the registry columns alone
 * (ISS-292248) — the caller (`AdditionalChargeGrids.tsx`) supplies them from the lines payload, the
 * currency master, and its own stylesheet, and they are spread verbatim into every cell's
 * `CellRenderContext`.
 */
export interface AdditionalChargeGridExtras {
  pcd: string | null;
  /** ISS-292248: per-container PCD for CONTAINER-level vendor types (mirrors `pcd`), sourced straight
   * from the lines payload — see `resolveDisplayPcd`. */
  pcdByContainer?: Record<string, string | null>;
  exchangeRates: Record<string, number>;
  thirdPartyVendors: Array<{ code: string; name: string }>;
  currencyOptions: string[];
  currencySelectClassName?: string;
  thirdPartyVendorSelectClassName?: string;
}

type CellRenderer = (
  column: AdditionalChargeColumn,
  line: AdditionalChargeLineDto,
  ctx: CellRenderContext
) => React.ReactNode;

/** Every plain field the registry can point a column at, generically typed text vs. date vs. money. */
const FIELD_KIND: Record<string, 'text' | 'date' | 'money'> = {
  blNumber: 'text',
  blDate: 'date',
  containerNumber: 'text',
  serviceStartDate: 'date',
  sacCode: 'text',
  rate: 'money',
  currency: 'text',
  thirdPartyInvoiceNumber: 'text',
  thirdPartyInvoiceDate: 'date',
  thirdPartyInvoiceValue: 'money',
  basicValue: 'money',
  cgst: 'money',
  sgst: 'money',
  igst: 'money',
  thirdPartyVendorCode: 'text',
  thirdPartyVendorName: 'text',
};

const renderPlainText = (value: string | null): React.ReactNode => value || '-';

const renderPlainDate = (value: string | null): React.ReactNode =>
  value ? moment(value).format(ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT) : '-';

/**
 * Generic text/date/money cell — editability resolved once, centrally, by
 * `isColumnEditableForLine`. Money is rendered and edited as a plain string (D8): no parsing, no
 * arithmetic, whatever the vendor types or the server returns is passed straight through.
 */
const renderGenericField: CellRenderer = (
  column,
  line,
  { draftEdits, functions, isReadOnly, exclusiveGroupMembers, mirrorGroupMembers }
) => {
  const entityColumn = (column.entityColumn || column.key) as keyof AdditionalChargeLineDto;
  const value = resolveLineFieldValue(line, draftEdits, entityColumn);
  const editable = isColumnEditableForLine(column, line, isReadOnly);
  const kind = FIELD_KIND[column.key] || 'text';

  if (!editable) {
    return kind === 'date' ? renderPlainDate(value) : renderPlainText(value);
  }

  const field = column.key as AdditionalChargeEditableFieldKey;

  if (kind === 'date') {
    return (
      <DatePicker
        allowClear
        format={ADDITIONAL_CHARGE_DATE_DISPLAY_FORMAT}
        placeholder={ADDITIONAL_CHARGE_GRID_LABELS.datePlaceholder}
        value={value ? moment(value) : undefined}
        onChange={(date) =>
          functions.onFieldChange(line.id, field, date ? date.format(ADDITIONAL_CHARGE_DATE_WIRE_FORMAT) : null)
        }
      />
    );
  }

  const locked = isExclusiveMemberLocked(column, line, draftEdits, exclusiveGroupMembers, mirrorGroupMembers);
  const placeholder =
    column.key === 'rate' ? ADDITIONAL_CHARGE_GRID_LABELS.ratePlaceholder : column.label;

  return (
    <Input
      value={value || ''}
      disabled={locked}
      placeholder={placeholder}
      onChange={(event) => functions.onFieldChange(line.id, field, event.target.value || null)}
    />
  );
};

/**
 * Attachment cell. Shows the last-saved attachment OR a locally-staged (not-yet-saved) upload — the
 * upload no longer persists on its own, so a staged file is what's shown until the next Save writes it
 * (ISS-291749). When a file is present an eye icon is the view affordance: hovering shows the file name
 * and clicking it opens the in-app preview (`FileViewer`), matching every other SE task's document
 * behaviour. Replacing a file is a bare upload icon in the same cell — no "Replace" text.
 * Icons are centered via the column's `align` (see `buildAntColumns`).
 */
const renderAttachment: CellRenderer = (
  column,
  line,
  { functions, isAttachmentMandatory, isReadOnly }
) => {
  const editable = isColumnEditableForLine(column, line, isReadOnly);
  const isUploading = functions.isUploadingLine(line.id);
  // A staged upload (this session, unsaved) always shows over the last-saved attachment.
  const attachment = functions.getPendingAttachment(line.id) || line.attachment;

  if (attachment) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: tokens.spacing.sm,
        }}
      >
        <a
          role="button"
          title={attachment.fileName}
          onClick={() => functions.onOpenAttachment(attachment)}
        >
          <Icon type="eye" style={{ fontSize: tokens.fontSize.h3 }} />
        </a>
        {editable && (
          <Upload
            showUploadList={false}
            disabled={isUploading}
            customRequest={({ file }: { file: File }) => functions.onUploadAttachment(line, file)}
          >
            <a role="button" title={ADDITIONAL_CHARGE_GRID_LABELS.replaceAttachment}>
              <Icon type={isUploading ? 'loading' : 'upload'} />
            </a>
          </Upload>
        )}
      </span>
    );
  }

  if (!editable) {
    return '-';
  }

  return (
    <Upload
      showUploadList={false}
      disabled={isUploading}
      customRequest={({ file }: { file: File }) => functions.onUploadAttachment(line, file)}
    >
      <a
        role="button"
        title={
          isAttachmentMandatory
            ? ADDITIONAL_CHARGE_GRID_LABELS.attachmentRequiredTooltip
            : ADDITIONAL_CHARGE_GRID_LABELS.uploadAttachmentTooltip
        }
      >
        <Icon type={isUploading ? 'loading' : 'upload'} />
        {isAttachmentMandatory && <Asterisk />}
      </a>
    </Upload>
  );
};

/**
 * Third-Party `3rd Party Invoice Value (INR)` — a read-only live total, never an editable or plain
 * persisted cell (ISS-291744). Renders Basic + CGST + SGST + IGST from the current draft edits so the
 * vendor sees the invoice value update as they type; the backend re-derives the authoritative value on
 * save (ISS-291743). Falls back to `-` until a money component is entered. This deliberately overrides
 * the generic `money` renderer that the `FIELD_KIND` loop would otherwise assign to this key.
 */
const renderThirdPartyInvoiceValue: CellRenderer = (_column, line, { draftEdits }) =>
  computeThirdPartyInvoiceTotal(line, draftEdits) ?? '-';

/**
 * Currency picker (ISS-292248, P9) — the same master-data currency list the inquiry bidding flow uses,
 * threaded in via `ctx.currencyOptions` rather than re-fetched here. Falls back to plain text once the
 * line is no longer editable, same as every other generic cell.
 */
const renderCurrency: CellRenderer = (column, line, ctx) => {
  const value = resolveDisplayCurrency(line, ctx.draftEdits);
  // When the engine priced this line, its currency is part of the resolved monetary pair (like the rate)
  // and is not vendor-editable — render it read-only so the currency can never contradict the resolved
  // rate shown alongside it (Codex review P2, decision F1).
  const engineResolvedCurrency = line.resolution?.rate?.currency != null;
  if (engineResolvedCurrency || !isColumnEditableForLine(column, line, ctx.isReadOnly)) {
    return renderPlainText(value);
  }
  return (
    <Select
      showSearch
      value={value || undefined}
      placeholder={column.label}
      className={ctx.currencySelectClassName}
      onChange={(next: string) => ctx.functions.onFieldChange(line.id, 'currency', next || null)}
    >
      {ctx.currencyOptions.map((code) => (
        <Select.Option key={code} value={code}>
          {code}
        </Select.Option>
      ))}
    </Select>
  );
};

/**
 * Third-Party vendor code, picked from the shipper org's vendor master (ISS-292248, P11). The paired
 * `thirdPartyVendorName` cell is never itself editable — it resolves read-only from whichever code is
 * currently selected, so the two cells always agree with what the master says, not with whatever the
 * vendor previously typed into a free-text name field.
 */
const renderVendorCode: CellRenderer = (column, line, ctx) => {
  const value = resolveLineFieldValue(line, ctx.draftEdits, 'thirdPartyVendorCode');
  if (!isColumnEditableForLine(column, line, ctx.isReadOnly)) {
    return renderPlainText(value);
  }
  return (
    <Select
      showSearch
      allowClear
      value={value || undefined}
      placeholder={column.label}
      className={ctx.thirdPartyVendorSelectClassName}
      onChange={(next: string | undefined) =>
        ctx.functions.onFieldChange(line.id, 'thirdPartyVendorCode', next || null)
      }
    >
      {ctx.thirdPartyVendors.map((vendor) => (
        <Select.Option key={vendor.code} value={vendor.code}>
          {vendor.code}
        </Select.Option>
      ))}
    </Select>
  );
};

/** Always read-only (ISS-292248): resolved from the vendor master by the current code, never typed. */
const renderVendorName: CellRenderer = (_column, line, ctx) => {
  const code = resolveLineFieldValue(line, ctx.draftEdits, 'thirdPartyVendorCode');
  const fromMaster = ctx.thirdPartyVendors.find((vendor) => vendor.code === code)?.name;
  return renderPlainText(fromMaster ?? line.thirdPartyVendorName);
};

/**
 * Read-only INR cell factory (ISS-292248) for the two virtual registry keys `amountInr` (Incidental /
 * Self-Reimbursement) and `invoiceValueInr` (Third-Party). Value sourcing now goes through
 * `resolveInrCell` (ISS-292248 Task 3, decision F1): the engine's live `resolution.baseAmount` wins
 * verbatim when present, else this delegates unchanged to the existing liveness/quantity-fallback/
 * rate-status logic the accessor falls back to. This renderer only decides how to paint the result, switching
 * on `rateStatus` (ISS-292248 review fix, CRITICAL 1) rather than the coarser `rateUnavailable` boolean,
 * so a newly-picked currency (`'pending-refresh'`) gets an honest, non-alarming tooltip instead of the
 * "cannot be submitted" wording that only genuinely belongs to `'unavailable'`.
 */
const renderInrCell = (kind: 'amount' | 'invoiceValue'): CellRenderer => (_column, line, ctx) => {
  const { value, rateStatus } = resolveInrCell(line, ctx.draftEdits, ctx.exchangeRates, kind);
  // No PCD means no rate DATE, so nothing can be converted regardless of currency — say that instead of
  // implying the currency is at fault (ISS-292248). "No PCD" must use the SAME fallback chain the PCD cell
  // shows (`resolveDisplayPcd`): for a CONTAINER-level charge the task-level `ctx.pcd` can be null while
  // the row's own per-container PCD is populated — checking only `ctx.pcd` would wrongly claim the PCD is
  // missing though the PCD cell shows a real date (ISS-292248 F2). Only reachable while the line is still
  // live; a frozen line carries its own baseAmount and never reports a rate status.
  const displayPcd = resolveDisplayPcd(line, ctx.pcd, ctx.pcdByContainer);
  if (rateStatus !== 'ok' && !displayPcd) {
    return (
      <Tooltip title={ADDITIONAL_CHARGE_GRID_LABELS.pcdMissingTooltip}>
        <span>
          <Icon type="info-circle" /> {ADDITIONAL_CHARGE_GRID_LABELS.pcdMissing}
        </span>
      </Tooltip>
    );
  }
  if (rateStatus === 'unavailable') {
    return (
      <Tooltip title={ADDITIONAL_CHARGE_GRID_LABELS.rateUnavailableTooltip}>
        <span>
          <Icon type="warning" /> {ADDITIONAL_CHARGE_GRID_LABELS.rateUnavailable}
        </span>
      </Tooltip>
    );
  }
  if (rateStatus === 'pending-refresh') {
    return (
      <Tooltip title={ADDITIONAL_CHARGE_GRID_LABELS.ratePendingRefreshTooltip}>
        <span>
          <Icon type="info-circle" /> {ADDITIONAL_CHARGE_GRID_LABELS.ratePendingRefresh}
        </span>
      </Tooltip>
    );
  }
  return value ?? '-';
};

/**
 * PCD (ISS-292248, P1/P4) — read-only always, D7 having been reversed: the task-level Price Calculation
 * Date is server-derived, never vendor-typed. Value sourcing now goes through `resolveDisplayPcd`
 * (ISS-292248 Task 3, decision F1): a frozen line always keeps its own stored `serviceStartDate`; a live
 * line prefers the engine's `resolution.pcd`, then the per-container PCD (`ctx.pcdByContainer`), then the
 * payload-level `ctx.pcd`. `resolveDisplayPcd` shares its liveness check (`isFxLive`) with
 * `resolveInrCell`'s fallback, so this and the INR cells can never disagree on which PCD a live figure
 * was converted at.
 */
const renderPcd: CellRenderer = (_column, line, ctx) =>
  renderPlainDate(resolveDisplayPcd(line, ctx.pcd, ctx.pcdByContainer));

/**
 * Rate cell (ISS-292248 Task 3, decision F1): the engine's `resolution.rate.rate` wins verbatim for a
 * QUOTE-sourced Incidental line the engine priced; otherwise this falls back to the existing
 * draft-edit-or-persisted value, via `resolveDisplayRate` — same lookup `renderGenericField`'s money
 * handling used before, just routed through the accessor. Editability is UNCHANGED: still resolved by
 * `isColumnEditableForLine` / `dynamicByRateSource` (a MANUAL-rate line stays editable, a BID line stays
 * read-only) — only the *displayed* value now goes through the accessor.
 */
const renderRateCell: CellRenderer = (
  column,
  line,
  { draftEdits, functions, isReadOnly, exclusiveGroupMembers, mirrorGroupMembers }
) => {
  const value = resolveDisplayRate(line, draftEdits);
  const editable = isColumnEditableForLine(column, line, isReadOnly);

  if (!editable) {
    return renderPlainText(value);
  }

  const locked = isExclusiveMemberLocked(column, line, draftEdits, exclusiveGroupMembers, mirrorGroupMembers);

  return (
    <Input
      value={value || ''}
      disabled={locked}
      placeholder={ADDITIONAL_CHARGE_GRID_LABELS.ratePlaceholder}
      onChange={(event) => functions.onFieldChange(line.id, 'rate', event.target.value || null)}
    />
  );
};

/**
 * Quantity cell (ISS-292248 Task 4). While a line carries a live `resolution.quantity`, this renders
 * each dim (`resolution.quantity.dims[]`) — `label ?? code` and its `final` — instead of a single
 * number. A dim is a numeric input, staged into `ctx.draftQuantityInputs` (collected into
 * `quantityInputs` on Save, see `buildSaveLines`), when `isColumnEditableForLine` says the column itself
 * is still editable for this line AND `isQuantityDimEditable` says the dim accepts an override; every
 * other dim renders read-only. When a dim carries BOTH `computed` and `override`, the computed value is
 * shown alongside (`(Computed: …)`) so the vendor can see what they overrode without losing the original
 * figure.
 *
 * A line with no live `resolution.quantity` (frozen, or the engine has nothing to say about this line —
 * every fixture before this task) falls back to `resolveDisplayQuantity`: a single read-only number,
 * exactly the pre-Task-4 rendering.
 */
const renderQuantityCell: CellRenderer = (column, line, ctx) => {
  const dims = line.resolution?.quantity?.dims;
  if (!dims || !dims.length) {
    return renderPlainText(resolveDisplayQuantity(line));
  }

  const columnEditable = isColumnEditableForLine(column, line, ctx.isReadOnly);
  const draftForLine = ctx.draftQuantityInputs[line.id] || {};

  return (
    <div>
      {dims.map((dim: AcQuantityDimResult) => {
        const label = dim.label ?? dim.code;
        const dimEditable = columnEditable && isQuantityDimEditable(dim);
        const showComputedAside = dim.computed != null && dim.override != null;
        return (
          <div
            key={dim.code}
            style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing.xs }}
          >
            <span>{`${label}:`}</span>
            {dimEditable ? (
              <Input
                type="number"
                value={draftForLine[dim.code] ?? dim.override ?? dim.final ?? ''}
                onChange={(event) =>
                  ctx.functions.onQuantityInputChange?.(line.id, dim.code, event.target.value)
                }
              />
            ) : (
              <span>{dim.final ?? '-'}</span>
            )}
            {showComputedAside && (
              <Tooltip title={`${ADDITIONAL_CHARGE_GRID_LABELS.quantityDimComputedPrefix}: ${dim.computed}`}>
                <span>{`(${ADDITIONAL_CHARGE_GRID_LABELS.quantityDimComputedPrefix}: ${dim.computed})`}</span>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** `"code — message"`, one per line error — the exact copy shown in the tooltip below AND (joined by
 * newlines) in the native `title` attribute the icon also carries, so the same content is queryable
 * without simulating a real hover (jsdom never fires the rc-tooltip mouseenter that would reveal the
 * antd overlay). */
const formatLineError = (error: AcLineError): string => `${error.code} — ${error.message}`;

/**
 * Rate-engine error indicator (ISS-292248 Task 5): a warning icon added to the existing `status` cell
 * — never a new column — mirroring the attachment cell's icon-only precedent (ISS-291744) so no column
 * width shifts. Shown whenever `lineErrors` is non-empty, regardless of the line's status (a live
 * DRAFT/REWORK line surfaces its engine `resolution.errors`; a frozen/legacy line falls back to the
 * wire-level `errors` union) — see `lineErrors`'s own doc comment.
 */
const renderStatus: CellRenderer = (_column, line) => {
  const errors = lineErrors(line);
  return (
    <span>
      <Tag color={LINE_STATUS_COLORS[line.status]}>{LINE_STATUS_LABELS[line.status]}</Tag>
      {(line.status === 'REJECTED' || line.status === 'REWORK') && line.rejectionRemark && (
        <Tooltip title={line.rejectionRemark}>
          <div>
            <Icon type="info-circle" /> {line.rejectionRemark}
          </div>
        </Tooltip>
      )}
      {errors.length > 0 && (
        <Tooltip
          title={
            <>
              {errors.map((error) => (
                <div key={error.code}>{formatLineError(error)}</div>
              ))}
            </>
          }
        >
          <span title={errors.map(formatLineError).join('\n')}>
            <Icon type="warning" style={{ color: tokens.colors.error }} />
          </span>
        </Tooltip>
      )}
    </span>
  );
};

/** columnKey → cell renderer. This map, not a per-tab switch, is the entire rendering surface. */
const CELL_RENDERERS: Record<string, CellRenderer> = {
  attachment: renderAttachment,
  status: renderStatus,
};

Object.keys(FIELD_KIND).forEach((key) => {
  CELL_RENDERERS[key] = renderGenericField;
});

// `thirdPartyInvoiceValue` is a computed read-only total, not a plain money cell — override the generic
// assignment the loop above just made (ISS-291744).
CELL_RENDERERS.thirdPartyInvoiceValue = renderThirdPartyInvoiceValue;

// ISS-292248: currency/vendor-code become Selects, vendor-name and PCD become read-only resolved cells
// (overriding the generic text/date assignments the loop above made), and the two virtual INR keys
// (never in FIELD_KIND — they are not real `AdditionalChargeLineDto` columns) get their own renderer.
CELL_RENDERERS.currency = renderCurrency;
CELL_RENDERERS.thirdPartyVendorCode = renderVendorCode;
CELL_RENDERERS.thirdPartyVendorName = renderVendorName;
CELL_RENDERERS.amountInr = renderInrCell('amount');
CELL_RENDERERS.invoiceValueInr = renderInrCell('invoiceValue');
CELL_RENDERERS.serviceStartDate = renderPcd;
// ISS-292248 Task 3: rate now routes through `resolveDisplayRate` (decision F1) rather than the generic
// money lookup the loop above assigned — the engine's live resolution can win over the persisted/draft
// value for a QUOTE-sourced line. Editability is untouched.
CELL_RENDERERS.rate = renderRateCell;
// ISS-292248 Task 4: `quantity` was never in `FIELD_KIND` — before this task it had no renderer at all and
// fell through `buildAntColumns`'s default (a bare `resolveLineFieldValue` plain-text lookup of the
// persisted column, ignoring any live resolution). It now gets its own renderer, same pattern as the
// other overrides above.
CELL_RENDERERS.quantity = renderQuantityCell;

/**
 * Turns one registry column into one antd `Table` column, dispatching purely on `column.key`.
 *
 * `extras` precedes the defaulted `isReadOnly` (ISS-292248 review fix, MINOR 6) — it used to follow it,
 * which made `isReadOnly`'s `= false` default dead: every real caller needs to pass `extras`, so every
 * call site was forced to also spell out `isReadOnly` explicitly just to reach it positionally.
 */
export const buildAntColumns = (
  columns: AdditionalChargeColumn[],
  draftEdits: AdditionalChargeLineDraftEdits,
  functions: AdditionalChargeCellFunctions,
  isAttachmentMandatory: boolean,
  extras: AdditionalChargeGridExtras,
  isReadOnly = false,
  /** ISS-292248 Task 4: defaulted so every existing call site (none of which staged a quantity-dim edit
   * before this task) keeps compiling and behaving unchanged. */
  draftQuantityInputs: AdditionalChargeQuantityDraftInputs = {}
): ColumnProps<AdditionalChargeLineDto>[] => {
  const exclusiveGroupMembers = buildExclusiveGroupMembers(columns);
  const mirrorGroupMembers = buildMirrorGroupMembers(columns);
  const ctx: CellRenderContext = {
    draftEdits,
    functions,
    isAttachmentMandatory,
    isReadOnly,
    exclusiveGroupMembers,
    mirrorGroupMembers,
    draftQuantityInputs,
    ...extras,
  };
  return columns.map((column) => ({
    key: column.key,
    dataIndex: column.entityColumn || column.key,
    title: column.label,
    // Attachment is a purely iconographic column — center the header and its icon(s); other columns
    // keep antd's default left alignment.
    align: column.key === 'attachment' ? ('center' as const) : undefined,
    render: (_value: unknown, line: AdditionalChargeLineDto) => {
      const renderer = CELL_RENDERERS[column.key];
      return renderer
        ? renderer(column, line, ctx)
        : renderPlainText(resolveLineFieldValue(line, draftEdits, column.key as keyof AdditionalChargeLineDto));
    },
  }));
};
